import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  LockIconGrey,
  LockIconSizeTable,
} from "../utils/LockIconforPermission";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { HrmsBranchSelect } from "./HrmsBranchSelect";
import Pagination from "./Pagination";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";

const ATTENDANCE_LOCK_ICON = "solar:lock-bold";

const ATTENDANCE_NO_ACCESS_MSG =
  "You do not have permission to view or edit attendance.";

const ATTENDANCE_EDIT_ONLY_MSG =
  "You can record punches, but existing punch times require view permission.";

function AttendanceNoAccessMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={ATTENDANCE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{ATTENDANCE_NO_ACCESS_MSG}</span>
    </span>
  );
}

function todayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

function toTimeInput(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const hmOnly = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hmOnly) {
    return `${hmOnly[1].padStart(2, "0")}:${hmOnly[2]}`;
  }
  const parsed = Date.parse(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isFinite(parsed)) {
    const d = new Date(parsed);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}:${m[2]}`;
}

function isPunchInType(typeRaw) {
  const type = String(typeRaw ?? "")
    .toUpperCase()
    .replace(/[\s-]/g, "_");
  return (
    type === "IN" ||
    type === "PUNCH_IN" ||
    type === "PUNCHIN" ||
    type === "CHECK_IN" ||
    type === "CHECKIN"
  );
}

function isPunchOutType(typeRaw) {
  const type = String(typeRaw ?? "")
    .toUpperCase()
    .replace(/[\s-]/g, "_");
  return (
    type === "OUT" ||
    type === "PUNCH_OUT" ||
    type === "PUNCHOUT" ||
    type === "CHECK_OUT" ||
    type === "CHECKOUT"
  );
}

function punchTimestamp(val) {
  if (val == null || val === "") return NaN;
  return Date.parse(String(val).trim().replace(" ", "T"));
}

function combineDateTime(date, time) {
  const t = String(time || "").trim();
  if (!date || !t) return null;
  return `${date} ${t}:00`;
}

function currentTimeHm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function groupFlatPunchesToEmployees(flatPunches) {
  const byUa = new Map();
  for (const p of flatPunches) {
    const ua = Number(p.user_admin_id ?? p.employee_id ?? p.ua_id);
    if (!Number.isFinite(ua) || ua <= 0) continue;
    if (!byUa.has(ua)) {
      byUa.set(ua, { user_admin_id: ua, punches: [] });
    }
    byUa.get(ua).punches.push(p);
  }
  return Array.from(byUa.values());
}

function extractPunchListEmployees(res) {
  const d = res?.data;
  /** API may return a bare array of punch rows, e.g. [{ user_admin_id, punch_type, punched_at, ... }]. */
  if (Array.isArray(d) && d.length > 0) {
    const sample = d[0];
    if (sample && typeof sample === "object" && Array.isArray(sample.punches)) {
      return d;
    }
    const ua = Number(
      sample?.user_admin_id ?? sample?.employee_id ?? sample?.ua_id,
    );
    const hasPunchTime =
      sample?.punched_at ??
      sample?.punch_time ??
      sample?.punch_at ??
      sample?.time;
    if (Number.isFinite(ua) && ua > 0 && hasPunchTime) {
      return groupFlatPunchesToEmployees(d);
    }
  }
  if (Array.isArray(d?.employees)) return d.employees;
  if (Array.isArray(d?.data?.employees)) return d.data.employees;
  if (Array.isArray(d?.employee_punches)) return d.employee_punches;
  if (Array.isArray(d?.punch_list)) return d.punch_list;
  if (Array.isArray(d?.punches)) return groupFlatPunchesToEmployees(d.punches);
  if (Array.isArray(d?.data?.punches)) {
    return groupFlatPunchesToEmployees(d.data.punches);
  }
  return [];
}

function extractLegacyAttendanceRows(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.attendance)) return d.attendance;
  if (Array.isArray(d?.records)) return d.records;
  return [];
}

function summarizePunches(punches) {
  const list = Array.isArray(punches) ? punches : [];
  let firstIn = null;
  let firstInId = null;
  let lastOut = null;
  let lastOutId = null;

  for (const p of list) {
    const at =
      p.punched_at ?? p.punch_time ?? p.punch_at ?? p.created_at ?? p.time;
    if (!at) continue;
    const ts = punchTimestamp(at);
    if (!Number.isFinite(ts)) continue;
    const typeRaw = p.punch_type ?? p.type ?? p.direction;
    if (isPunchInType(typeRaw)) {
      if (firstIn == null || ts < punchTimestamp(firstIn)) {
        firstIn = at;
        firstInId = p.id != null ? Number(p.id) : null;
      }
    } else if (isPunchOutType(typeRaw)) {
      if (lastOut == null || ts > punchTimestamp(lastOut)) {
        lastOut = at;
        lastOutId = p.id != null ? Number(p.id) : null;
      }
    }
  }

  return {
    punch_in: toTimeInput(firstIn),
    punch_out: toTimeInput(lastOut),
    punch_in_id: firstInId,
    punch_out_id: lastOutId,
  };
}

function summarizeLegacyRow(row) {
  const punchIn = toTimeInput(
    row.punch_in ?? row.punch_in_time ?? row.in_time ?? row.check_in,
  );
  const punchOut = toTimeInput(
    row.punch_out ?? row.punch_out_time ?? row.out_time ?? row.check_out,
  );
  return {
    punch_in: punchIn,
    punch_out: punchOut,
    punch_in_id:
      row.punch_in_id != null
        ? Number(row.punch_in_id)
        : row.in_punch_id != null
          ? Number(row.in_punch_id)
          : null,
    punch_out_id:
      row.punch_out_id != null
        ? Number(row.punch_out_id)
        : row.out_punch_id != null
          ? Number(row.out_punch_id)
          : null,
    attendance_id: row.id != null ? Number(row.id) : null,
  };
}

function buildEntriesFromPunchEmployees(employees, punchListEmployees) {
  const byUa = new Map();
  for (const emp of punchListEmployees) {
    const ua = Number(emp.user_admin_id ?? emp.employee_id ?? emp.ua_id);
    if (!Number.isFinite(ua) || ua <= 0) continue;
    byUa.set(ua, summarizePunches(emp.punches));
  }
  return mergeEmployeeEntries(employees, byUa, "punch");
}

function buildEntriesFromLegacyRows(employees, legacyRows) {
  const byUa = new Map();
  for (const row of legacyRows) {
    const ua = Number(row.user_admin_id ?? row.employee_id ?? row.ua_id);
    if (!Number.isFinite(ua) || ua <= 0) continue;
    byUa.set(ua, summarizeLegacyRow(row));
  }
  return mergeEmployeeEntries(employees, byUa, "legacy");
}

function mergeEmployeeEntries(employees, byUa, source) {
  const next = {};
  for (const emp of employees) {
    const ua = Number(emp.ua_id);
    const rec = byUa.get(ua) || {};
    const punchIn = rec.punch_in ?? "";
    const punchOut = rec.punch_out ?? "";
    next[ua] = {
      punch_in_id: rec.punch_in_id ?? null,
      punch_out_id: rec.punch_out_id ?? null,
      attendance_id: source === "legacy" ? (rec.attendance_id ?? null) : null,
      punch_in: punchIn,
      punch_out: punchOut,
      saved_punch_in: punchIn,
      saved_punch_out: punchOut,
    };
  }
  return next;
}

/** Daily punch workflow phase for one employee row (from merged `entries[ua]`). */
function getAttendancePhase(entry) {
  const row = entry || {};
  const hasIn = Boolean(String(row.punch_in ?? "").trim());
  const hasOut = Boolean(String(row.punch_out ?? "").trim());
  if (!hasIn) return "pending_in";
  if (!hasOut) return "pending_out";
  return "complete";
}

function pickRicherEntries(primary, fallback) {
  if (!primary) return fallback;
  if (!fallback) return primary;
  let primaryScore = 0;
  let fallbackScore = 0;
  for (const row of Object.values(primary)) {
    if (row.punch_in) primaryScore += 1;
    if (row.punch_out) primaryScore += 1;
  }
  for (const row of Object.values(fallback)) {
    if (row.punch_in) fallbackScore += 1;
    if (row.punch_out) fallbackScore += 1;
  }
  return fallbackScore > primaryScore ? fallback : primary;
}

async function fetchAttendanceEntries(branchId, attendanceDate, employees) {
  const date = String(attendanceDate).trim();
  const empty = buildEntriesFromPunchEmployees(employees, []);

  const punchRes = await hrmsApi("attendance_punch_list", {
    branch_id: Number(branchId),
    work_date: date,
  });
  let fromPunch = null;
  if (isErpApiSuccess(punchRes)) {
    const punchEmployees = extractPunchListEmployees(punchRes);
    fromPunch = buildEntriesFromPunchEmployees(employees, punchEmployees);
    const hasTimes = Object.values(fromPunch).some(
      (r) => r.punch_in || r.punch_out,
    );
    if (!hasTimes && punchEmployees.length === 0) {
      const altRes = await hrmsApi("attendance_punch_list", {
        branch_id: Number(branchId),
        work_date: date,
      });
      if (isErpApiSuccess(altRes)) {
        fromPunch = buildEntriesFromPunchEmployees(
          employees,
          extractPunchListEmployees(altRes),
        );
      }
    }
  }

  const listRes = await hrmsApi("attendance_punch_list", {
    branch_id: Number(branchId),
    work_date: date,
  });
  let fromLegacy = null;
  if (isErpApiSuccess(listRes)) {
    fromLegacy = buildEntriesFromLegacyRows(
      employees,
      extractLegacyAttendanceRows(listRes),
    );
  }

  const merged = pickRicherEntries(fromPunch, fromLegacy);
  return { entries: merged ?? empty, punchRes, listRes };
}

function punchBulkErrors(data) {
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  return errors
    .map((e) => {
      if (e == null) return "";
      if (typeof e === "string") return e;
      return String(e.message || e.error || JSON.stringify(e));
    })
    .filter(Boolean)
    .join("; ");
}

async function deletePunch(branchId, punchId) {
  if (punchId == null) return true;
  const res = await hrmsApi("attendance_punch_delete", {
    branch_id: Number(branchId),
    id: Number(punchId),
  });
  if (!isErpApiSuccess(res)) {
    toast.error(res?.message || "Could not remove previous punch.");
    return false;
  }
  return true;
}

async function recordPunchBulk(
  branchId,
  apiType,
  punchedAt,
  userAdminIds,
  note,
) {
  const body = {
    branch_id: Number(branchId),
    punched_at: punchedAt,
    user_admin_ids: userAdminIds.map(Number),
  };
  if (note) body.note = note;
  return hrmsApi(apiType, body);
}

export function AttendanceTab({
  branchId,
  onBranchChange,
  branchOptions,
  branchesLoading,
}) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_ATTENDANCE_VIEW);
  const canEdit = hasPermission(PERMISSIONS.HRMS_ATTENDANCE_EDIT);
  const hasAnyAccess = canView || canEdit;

  const [attendanceDate, setAttendanceDate] = useState(todayIsoDate);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [entries, setEntries] = useState({});
  const [selectedUa, setSelectedUa] = useState(() => new Set());
  const [actionTime, setActionTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [punching, setPunching] = useState(null);
  /** Reveal row checkboxes on hover, or while the row stays selected after checking. */
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  const selectAllCheckboxRef = useRef(null);

  const { employees, loading: empLoading } = useHrmsEmployees(
    hasAnyAccess ? branchId : null,
  );

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      ),
    [employees],
  );

  const displayEmployees = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedEmployees.slice(start, start + itemsPerPage);
  }, [sortedEmployees, currentPage, itemsPerPage]);

  const loadAttendance = useCallback(async () => {
    if (!branchId || !hasAnyAccess) {
      setEntries({});
      return;
    }
    if (!canView) {
      setEntries(buildEntriesFromPunchEmployees(employees, []));
      return;
    }
    if (empLoading) return;

    setLoading(true);
    try {
      const { entries, punchRes, listRes } = await fetchAttendanceEntries(
        branchId,
        attendanceDate,
        employees,
      );
      setEntries(entries);
      if (
        !isErpApiSuccess(punchRes) &&
        !isErpApiSuccess(listRes) &&
        punchRes?.message
      ) {
        toast.error(punchRes.message);
      } else if (
        !isErpApiSuccess(punchRes) &&
        !isErpApiSuccess(listRes) &&
        listRes?.message
      ) {
        toast.error(listRes.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load attendance.");
      setEntries(buildEntriesFromPunchEmployees(employees, []));
    } finally {
      setLoading(false);
    }
  }, [branchId, attendanceDate, canView, hasAnyAccess, employees, empLoading]);

  useEffect(() => {
    void loadAttendance();
  }, [loadAttendance]);

  useEffect(() => {
    setSelectedUa(new Set());
    setActionTime("");
    setHoveredRow(null);
    setCurrentPage(1);
  }, [branchId, attendanceDate]);

  const selectedIds = useMemo(
    () =>
      sortedEmployees
        .map((emp) => Number(emp.ua_id))
        .filter((ua) => selectedUa.has(ua)),
    [sortedEmployees, selectedUa],
  );

  const allSelected =
    sortedEmployees.length > 0 && selectedIds.length === sortedEmployees.length;

  const selectedCount = selectedIds.length;

  const punchButtonState = useMemo(() => {
    if (selectedCount === 0) {
      return {
        showIn: true,
        showOut: true,
        disableIn: true,
        disableOut: true,
        inTooltip: "Select employees first",
        outTooltip: "Select employees first",
      };
    }

    const phases = selectedIds.map((ua) => getAttendancePhase(entries[ua]));
    const uniquePhases = new Set(phases);

    if (uniquePhases.size > 1) {
      const msg =
        "Mixed statuses selected — pick employees that share the same attendance state.";
      return {
        showIn: true,
        showOut: true,
        disableIn: true,
        disableOut: true,
        inTooltip: msg,
        outTooltip: msg,
      };
    }

    const phase = phases[0];
    if (phase === "pending_in") {
      return {
        showIn: true,
        showOut: false,
        disableIn: false,
        disableOut: true,
        inTooltip: null,
        outTooltip: null,
      };
    }
    if (phase === "pending_out") {
      return {
        showIn: false,
        showOut: true,
        disableIn: true,
        disableOut: false,
        inTooltip: null,
        outTooltip: null,
      };
    }

    const doneMsg =
      "Already completed punch in and punch out for the selected employee(s).";
    return {
      showIn: true,
      showOut: true,
      disableIn: true,
      disableOut: true,
      inTooltip: doneMsg,
      outTooltip: doneMsg,
    };
  }, [selectedCount, selectedIds, entries]);

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) {
      el.indeterminate =
        selectedCount > 0 && selectedCount < sortedEmployees.length;
    }
  }, [selectedCount, sortedEmployees.length]);

  const toggleSelect = (ua) => {
    setSelectedUa((prev) => {
      const next = new Set(prev);
      if (next.has(ua)) next.delete(ua);
      else next.add(ua);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUa(new Set());
      return;
    }
    setSelectedUa(new Set(sortedEmployees.map((emp) => Number(emp.ua_id))));
  };

  const resolveActionTimestamp = () => {
    const time = String(actionTime || "").trim() || currentTimeHm();
    const punchedAt = combineDateTime(attendanceDate, time);
    if (!punchedAt) {
      toast.error("Enter a valid punch time.");
      return null;
    }
    return punchedAt;
  };

  const punchSelected = async (direction) => {
    if (!canEdit || !branchId) {
      toast.error("You do not have permission to edit attendance.");
      return;
    }
    if (!selectedIds.length) {
      toast.error("Select at least one employee.");
      return;
    }

    const punchedAt = resolveActionTimestamp();
    if (!punchedAt) return;

    const apiType =
      direction === "in"
        ? "attendance_punch_in_bulk"
        : "attendance_punch_out_bulk";
    const idField = direction === "in" ? "punch_in_id" : "punch_out_id";

    setPunching(direction);
    try {
      for (const ua of selectedIds) {
        const row = entries[ua];
        const existingId = row?.[idField];
        if (existingId != null) {
          const ok = await deletePunch(branchId, existingId);
          if (!ok) return;
        }
      }

      const res = await recordPunchBulk(
        branchId,
        apiType,
        punchedAt,
        selectedIds,
      );
      if (!isErpApiSuccess(res)) {
        toast.error(
          res?.message ||
            (direction === "in"
              ? "Could not record punch-in."
              : "Could not record punch-out."),
        );
        const errMsg = punchBulkErrors(res?.data);
        if (errMsg) toast.error(errMsg);
        return;
      }

      const created = Number(res?.data?.created ?? selectedIds.length);
      toast.success(
        hrmsSuccessMessage(
          res,
          direction === "in"
            ? `Punch-in recorded for ${created} employee(s).`
            : `Punch-out recorded for ${created} employee(s).`,
        ),
      );
      setSelectedUa(new Set());
      await loadAttendance();
    } catch (err) {
      hrmsErrorToast(
        err,
        direction === "in"
          ? "Could not record punch-in."
          : "Could not record punch-out.",
      );
    } finally {
      setPunching(null);
    }
  };

  const showSelectColumn = canEdit && hasAnyAccess;
  const colCount = 3 + (showSelectColumn ? 1 : 0);
  const tableBusy = loading || empLoading;
  const filterControlClass = buttonTableStyle === "square" ? "rounded-0" : "";

  const actionBusy = punching != null || tableBusy;

  const punchInDisabled = actionBusy || punchButtonState.disableIn;
  const punchOutDisabled = actionBusy || punchButtonState.disableOut;
  const punchInTitle =
    actionBusy || !punchButtonState.disableIn
      ? undefined
      : (punchButtonState.inTooltip ?? undefined);
  const punchOutTitle =
    actionBusy || !punchButtonState.disableOut
      ? undefined
      : (punchButtonState.outTooltip ?? undefined);

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="d-flex align-items-center gap-3 flex-nowrap flex-wrap">
          <HrmsBranchSelect
            branchId={branchId}
            onChangeBranchId={onBranchChange}
            branchOptions={branchOptions}
            loading={branchesLoading}
            buttonTableStyle={buttonTableStyle}
            disabled={!hasAnyAccess}
          />
          <Form.Control
            type="date"
            value={attendanceDate}
            onChange={(ev) => setAttendanceDate(ev.target.value)}
            disabled={!hasAnyAccess}
            aria-label="Attendance date"
            className={`flex-shrink-0 ${filterControlClass}`}
            style={{ width: 160, minHeight: 44 }}
          />
        </div>
      </div>

      <div className="col-12">
        <div
          className={`border position-relative ${cardClass} ${
            buttonTableStyle === "square" ? "rounded-0" : ""
          }`}
        >
          {!hasAnyAccess ? (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-90"
              style={{ zIndex: 5 }}
            >
              <AttendanceNoAccessMessage />
            </div>
          ) : null}

          {canEdit &&
          branchId &&
          sortedEmployees.length > 0 &&
          selectedCount > 0 ? (
            <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 px-3 py-3 border-bottom">
              <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Label
                    className="small text-muted mb-0 text-nowrap"
                    htmlFor="hrms-attendance-punch-time"
                  >
                    Punch time
                  </Form.Label>
                  <Form.Control
                    id="hrms-attendance-punch-time"
                    type="time"
                    value={actionTime}
                    onChange={(ev) => setActionTime(ev.target.value)}
                    disabled={actionBusy}
                    className={`flex-shrink-0 ${buttonClass}`}
                    aria-label="Punch time for selected employees"
                    title="Leave empty to use the current time when punching"
                    style={{ width: 130, minHeight: 40 }}
                  />
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {punchButtonState.showIn ? (
                    <Button
                      type="button"
                      variant="primary"
                      className={buttonClass}
                      disabled={punchInDisabled}
                      title={punchInTitle}
                      style={{ minHeight: 40 }}
                      onClick={() => void punchSelected("in")}
                    >
                      {punching === "in" ? (
                        "Saving…"
                      ) : (
                        <>
                          <Icon
                            icon="solar:login-2-bold"
                            className="me-1"
                            width={18}
                          />
                          Punch in
                        </>
                      )}
                    </Button>
                  ) : null}
                  {punchButtonState.showOut ? (
                    <Button
                      type="button"
                      variant="outline-primary"
                      className={buttonClass}
                      disabled={punchOutDisabled}
                      title={punchOutTitle}
                      style={{ minHeight: 40 }}
                      onClick={() => void punchSelected("out")}
                    >
                      {punching === "out" ? (
                        "Saving…"
                      ) : (
                        <>
                          <Icon
                            icon="solar:logout-2-bold"
                            className="me-1"
                            width={18}
                          />
                          Punch out
                        </>
                      )}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {!canView && canEdit && branchId ? (
            <div className="px-3 py-2 border-bottom small text-muted">
              {ATTENDANCE_EDIT_ONLY_MSG}
            </div>
          ) : null}

          <div className="table-responsive">
            <table
              className={`table text-nowrap mb-0 align-middle ${tableClass} ${
                buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
            >
              <thead className="text-dark fs-4">
                <tr
                  onMouseEnter={() => setIsHeaderHovered(true)}
                  onMouseLeave={() => setIsHeaderHovered(false)}
                >
                  {showSelectColumn ? (
                    <th style={{ width: "2.75rem" }}>
                      <div
                        style={{
                          visibility:
                            isHeaderHovered || selectedCount > 0 || allSelected
                              ? "visible"
                              : "hidden",
                        }}
                      >
                        <Form.Check
                          ref={selectAllCheckboxRef}
                          type="checkbox"
                          className="mb-0"
                          checked={allSelected}
                          disabled={actionBusy || !sortedEmployees.length}
                          onChange={toggleSelectAll}
                          aria-label="Select all employees"
                        />
                      </div>
                    </th>
                  ) : null}
                  <th>Employee</th>
                  <th style={{ minWidth: "8rem" }}>Punch-in</th>
                  <th style={{ minWidth: "8rem" }}>Punch-out</th>
                </tr>
              </thead>
              <tbody>
                {!hasAnyAccess ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="text-center py-5 text-muted"
                    >
                      <AttendanceNoAccessMessage />
                    </td>
                  </tr>
                ) : !branchId ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="text-center py-5 text-muted"
                    >
                      Select a branch to view employees.
                    </td>
                  </tr>
                ) : tableBusy ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="text-center py-5 text-muted"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : sortedEmployees.length > 0 ? (
                  displayEmployees.map((emp) => {
                    const ua = Number(emp.ua_id);
                    const row = entries[ua] || {
                      punch_in: "",
                      punch_out: "",
                    };
                    const isSelected = selectedUa.has(ua);
                    return (
                      <tr
                        key={ua}
                        onMouseEnter={() => setHoveredRow(ua)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        {showSelectColumn ? (
                          <td
                            className="align-middle"
                            style={{ width: "2.75rem" }}
                          >
                            <div
                              style={{
                                visibility:
                                  hoveredRow === ua || isSelected
                                    ? "visible"
                                    : "hidden",
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                className="mb-0"
                                checked={isSelected}
                                disabled={actionBusy}
                                onChange={() => toggleSelect(ua)}
                                aria-label={`Select ${emp.name}`}
                              />
                            </div>
                          </td>
                        ) : null}
                        <td>{String(emp.name || "").trim() || "—"}</td>
                        <td>
                          {row.punch_in ? (
                            <span className="fw-medium">{row.punch_in}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {row.punch_out ? (
                            <span className="fw-medium">{row.punch_out}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="text-center py-5 text-muted"
                    >
                      No employees found for this branch.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {hasAnyAccess && sortedEmployees.length > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={sortedEmployees.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
